import { useState } from "react";
import { Trash2 } from "lucide-react";
import { makeId, saveRecord } from "../lib/db";
import { THOUGHT_CATEGORIES } from "../lib/categories";
import { dateInputValue, nowIso } from "../lib/time";
import type { Thought, ThoughtCategory, ThoughtStatus } from "../types";
import { Button, Field, Modal } from "./ui";

export function ThoughtEditor({
  thought,
  onClose,
  onSaved,
  onDelete
}: {
  thought?: Thought;
  onClose: () => void;
  onSaved: (thought: Thought) => void;
  onDelete?: (thought: Thought) => void;
}) {
  const [title, setTitle] = useState(thought?.title ?? "");
  const [body, setBody] = useState(thought?.body ?? "");
  const [facts, setFacts] = useState(thought?.facts ?? "");
  const [judgment, setJudgment] = useState(thought?.judgment ?? "");
  const [evidence, setEvidence] = useState(thought?.evidence ?? "");
  const [nextStep, setNextStep] = useState(thought?.nextStep ?? "");
  const [reviewAt, setReviewAt] = useState(dateInputValue(thought?.reviewAt));
  const [status, setStatus] = useState<ThoughtStatus>(thought?.status ?? "open");
  const [category, setCategory] = useState<ThoughtCategory>(
    thought?.category ?? "other"
  );
  const [error, setError] = useState("");

  async function save() {
    if (!title.trim() && !body.trim()) {
      setError("至少写下一句想法。");
      return;
    }
    const timestamp = nowIso();
    const next: Thought = {
      id: thought?.id ?? makeId(),
      kind: "thought",
      title: title.trim() || body.trim().split(/\n/)[0].slice(0, 60),
      body: body.trim(),
      createdAt: thought?.createdAt ?? timestamp,
      updatedAt: timestamp,
      revision: thought?.revision ?? 0,
      deletedAt: thought?.deletedAt,
      sourceIds: thought?.sourceIds,
      status,
      category,
      facts: facts.trim(),
      judgment: judgment.trim(),
      evidence: evidence.trim(),
      nextStep: nextStep.trim(),
      reviewAt: reviewAt || undefined,
      keyPoints: thought?.keyPoints,
      transcript: thought?.transcript,
      sourceType: thought?.sourceType ?? "manual",
      sourceUrl: thought?.sourceUrl,
      sourceTitle: thought?.sourceTitle,
      convertedToId: thought?.convertedToId
    };
    const saved = await saveRecord(next);
    onSaved(saved);
    onClose();
  }

  return (
    <Modal
      title={thought ? "整理思考" : "记录思考"}
      eyebrow="关键思考"
      onClose={onClose}
      size="large"
      footer={
        <>
          {thought && onDelete && (
            <Button type="button" variant="ghost" className="footer-delete" onClick={() => onDelete(thought)}>
              <Trash2 size={16} /> 删除
            </Button>
          )}
          <Button type="button" onClick={onClose}>
            取消
          </Button>
          <Button type="button" variant="primary" onClick={() => void save()}>
            保存
          </Button>
        </>
      }
    >
      <div className="editor-form">
        <div className="form-grid three">
          <Field label="标题">
            <input
              className="input input-large"
              autoFocus
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="给这条思考一个容易找回来的名字"
            />
          </Field>
          <Field label="归属">
            <select
              className="input"
              value={category}
              onChange={(event) => setCategory(event.target.value as ThoughtCategory)}
            >
              {THOUGHT_CATEGORIES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="状态">
            <select className="input" value={status} onChange={(event) => setStatus(event.target.value as ThoughtStatus)}>
              <option value="open">待思考</option>
              <option value="acted">已行动</option>
              <option value="archived">已归档</option>
            </select>
          </Field>
        </div>
        <Field label="原始想法">
          <textarea
            className="textarea thought-body-input"
            rows={7}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder="先按自己的语言写，不必一次想清楚。"
          />
        </Field>
        {thought?.transcript && thought.transcript !== thought.body && (
          <details className="structured-panel">
            <summary>查看原始转写或粘贴材料</summary>
            <textarea
              className="textarea"
              rows={10}
              value={thought.transcript}
              readOnly
            />
            {thought.sourceUrl && (
              <a className="source-link" href={thought.sourceUrl} target="_blank" rel="noreferrer">
                {thought.sourceTitle || thought.sourceUrl}
              </a>
            )}
          </details>
        )}
        <details className="structured-panel" open={Boolean(facts || judgment || evidence)}>
          <summary>把想法再想清楚一点（可选）</summary>
          <div className="form-grid">
            <Field label="已知事实" hint="哪些是已确认的，不是猜测？">
              <textarea className="textarea" rows={4} value={facts} onChange={(event) => setFacts(event.target.value)} />
            </Field>
            <Field label="我的判断" hint="我倾向于相信什么？">
              <textarea
                className="textarea"
                rows={4}
                value={judgment}
                onChange={(event) => setJudgment(event.target.value)}
              />
            </Field>
          </div>
          <div className="form-grid">
            <Field label="依据与反证" hint="还有什么证据缺失？">
              <textarea
                className="textarea"
                rows={4}
                value={evidence}
                onChange={(event) => setEvidence(event.target.value)}
              />
            </Field>
            <Field label="下一步与复核日期">
              <textarea
                className="textarea"
                rows={2}
                value={nextStep}
                onChange={(event) => setNextStep(event.target.value)}
                placeholder="需要验证什么？"
              />
              <input
                className="input"
                type="date"
                value={reviewAt}
                onChange={(event) => setReviewAt(event.target.value)}
              />
            </Field>
          </div>
        </details>
        {error && <p className="form-error">{error}</p>}
      </div>
    </Modal>
  );
}
